import axios from 'axios';

const MARINE_URL = 'https://marine-api.open-meteo.com/v1/marine';
const OPENWEATHER_URL = 'https://api.openweathermap.org/data/2.5/forecast';
const NOAA_BASE_URL = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter';

const OPENWEATHER_API_KEY = '37e122dacadb8b1bac86d0a45a1a51c6';

// Fallback estimate for non-US locations where NOAA won't have data
const getEstimatedWaterTemp = (lat) => {
    const absLat = Math.abs(lat);
    const month = new Date().getMonth();

    let baseTemp;
    if (absLat < 23.5)      baseTemp = 29;
    else if (absLat < 40)   baseTemp = 24.5;
    else if (absLat < 50)   baseTemp = 19;
    else                    baseTemp = 13;

    let adj = 0;
    if (lat > 0) {
        if (month >= 5 && month <= 8)       adj =  2;
        else if (month >= 11 || month <= 2) adj = -4;
        else if (month >= 3 && month <= 4)  adj = -1;
        else                                adj =  1;
    } else {
        if (month >= 11 || month <= 2)      adj =  2;
        else if (month >= 5 && month <= 8)  adj = -4;
        else if (month >= 9 && month <= 10) adj = -1;
        else                                adj =  1;
    }

    return baseTemp + adj;
};

const findNearestStation = async (lat, lon, type) => {
    try {
        const res = await axios.get('https://api.tidesandcurrents.noaa.gov/mdapi/prod/webapi/stations.json', {
            params: { type, units: 'english' }
        });

        if (!res.data || !res.data.stations) return null;

        let closest = null;
        let minDist = Infinity;

        res.data.stations.forEach(station => {
            const dist = Math.sqrt(
                Math.pow(lat - parseFloat(station.lat), 2) +
                Math.pow(lon - parseFloat(station.lng), 2)
            );
            if (dist < minDist) { minDist = dist; closest = station; }
        });

        return minDist < 2.0 ? closest : null;
    } catch (err) {
        console.error(`Error finding ${type} station:`, err);
        return null;
    }
};

// Known NOAA water temperature station IDs (physical oceanographic buoys)
// These are real-time stations that report water_temperature and are CORS-accessible
const NOAA_TEMP_STATIONS = [
    { id: '8638610', name: 'Sewells Point, VA',       lat: 36.947,  lon: -76.330 },
    { id: '8651370', name: 'Duck, NC',                lat: 36.183,  lon: -75.747 },
    { id: '8656483', name: 'Beaufort, NC',             lat: 34.718,  lon: -76.667 },
    { id: '8658120', name: 'Wilmington, NC',           lat: 34.227,  lon: -77.954 },
    { id: '8720218', name: 'Mayport, FL',              lat: 30.397,  lon: -81.428 },
    { id: '8723214', name: 'Virginia Key, FL',         lat: 25.731,  lon: -80.162 },
    { id: '8726520', name: 'St. Petersburg, FL',       lat: 27.760,  lon: -82.627 },
    { id: '8443970', name: 'Boston, MA',               lat: 42.355,  lon: -71.047 },
    { id: '8518750', name: 'Battery, NY',              lat: 40.700,  lon: -74.014 },
    { id: '8534720', name: 'Atlantic City, NJ',        lat: 39.355,  lon: -74.418 },
    { id: '8545240', name: 'Philadelphia, PA',         lat: 39.930,  lon: -75.141 },
    { id: '9410230', name: 'La Jolla, CA',             lat: 32.867,  lon: -117.257},
    { id: '9411340', name: 'Santa Barbara, CA',        lat: 34.408,  lon: -119.690},
    { id: '9414290', name: 'San Francisco, CA',        lat: 37.806,  lon: -122.465},
    { id: '9447130', name: 'Seattle, WA',              lat: 47.603,  lon: -122.340},
    { id: '1612340', name: 'Honolulu, HI',             lat: 21.307,  lon: -157.867},
];

const getNearestTempStation = (lat, lon) => {
    let closest = null;
    let minDist = Infinity;
    NOAA_TEMP_STATIONS.forEach(s => {
        const d = Math.sqrt(Math.pow(lat - s.lat, 2) + Math.pow(lon - s.lon, 2));
        if (d < minDist) { minDist = d; closest = s; }
    });
    return minDist < 15 ? closest : null; // only use if within ~15 degrees
};

const getWaterTempFromNOAA = async (stationId) => {
    try {
        const now = new Date();
        const formatDate = (d) =>
            d.getFullYear() +
            (d.getMonth() + 1).toString().padStart(2, '0') +
            d.getDate().toString().padStart(2, '0');

        const res = await axios.get(NOAA_BASE_URL, {
            params: {
                station:    stationId,
                product:    'water_temperature',
                begin_date: formatDate(now),
                end_date:   formatDate(now),
                units:      'metric',
                time_zone:  'lst_ldt',
                format:     'json',
                application:'SurfForecast'
            }
        });

        if (res.data && res.data.data && res.data.data.length > 0) {
            const readings = res.data.data
                .map(d => parseFloat(d.v))
                .filter(v => !isNaN(v) && v > -5 && v < 40);
            if (readings.length > 0) {
                const avg = readings.reduce((a, b) => a + b, 0) / readings.length;
                console.log(`NOAA water temp (${stationId}): ${avg.toFixed(1)}°C`);
                return avg;
            }
        }
        return null;
    } catch (err) {
        console.error('Error fetching NOAA water temp:', err);
        return null;
    }
};

const getTideData = async (stationId) => {
    try {
        const now = new Date();
        const endDate = new Date();
        endDate.setDate(now.getDate() + 7);

        const formatDate = (date) =>
            date.getFullYear() +
            (date.getMonth() + 1).toString().padStart(2, '0') +
            date.getDate().toString().padStart(2, '0');

        const response = await axios.get(NOAA_BASE_URL, {
            params: {
                station:    stationId,
                product:    'predictions',
                datum:      'MLLW',
                begin_date: formatDate(now),
                end_date:   formatDate(endDate),
                interval:   'hilo',
                units:      'english',
                time_zone:  'lst_ldt',
                format:     'json',
                application:'SurfForecast'
            }
        });

        if (response.data && response.data.predictions) {
            return response.data.predictions.map(p => ({
                time:   p.t,
                height: parseFloat(p.v),
                type:   p.type === 'H' ? 'High' : 'Low'
            }));
        }

        return null;
    } catch (error) {
        console.error('Error fetching tide data:', error);
        return null;
    }
};

const processOpenWeatherData = (openWeatherData, marineTimeArray) => {
    const windData = {
        speed:     new Array(marineTimeArray.length).fill(0),
        direction: new Array(marineTimeArray.length).fill(0)
    };

    if (openWeatherData.list) {
        openWeatherData.list.forEach((item, index) => {
            const hourIndex = index * 3;
            if (hourIndex < windData.speed.length) {
                windData.speed[hourIndex]     = item.wind.speed;
                windData.direction[hourIndex] = item.wind.deg;
            }
        });

        for (let i = 1; i < windData.speed.length; i++) {
            if (windData.speed[i] === 0 && i > 0) {
                const prevIndex = Math.floor((i - 1) / 3) * 3;
                const nextIndex = Math.min(Math.ceil(i / 3) * 3, windData.speed.length - 1);

                if (windData.speed[prevIndex] && windData.speed[nextIndex]) {
                    const ratio = (i - prevIndex) / (nextIndex - prevIndex);
                    windData.speed[i]     = windData.speed[prevIndex] + (windData.speed[nextIndex] - windData.speed[prevIndex]) * ratio;
                    windData.direction[i] = windData.direction[prevIndex] + (windData.direction[nextIndex] - windData.direction[prevIndex]) * ratio;
                } else {
                    windData.speed[i]     = windData.speed[prevIndex] || 5;
                    windData.direction[i] = windData.direction[prevIndex] || 180;
                }
            }
        }
    }

    return windData;
};

export const getSurfForecast = async (lat, lon) => {
    try {
        // Fetch marine + wind in parallel
        const [marineResponse, weatherResponse] = await Promise.all([
            axios.get(MARINE_URL, {
                params: {
                    latitude:  lat,
                    longitude: lon,
                    hourly:    'wave_height,wave_direction,wave_period',
                    timezone:  'auto'
                }
            }),
            axios.get(OPENWEATHER_URL, {
                params: {
                    lat:   lat,
                    lon:   lon,
                    appid: OPENWEATHER_API_KEY,
                    units: 'metric'
                }
            })
        ]);

        let tideStation = null;
        if (marineResponse.data && marineResponse.data.hourly) {
            const hourlyLength = marineResponse.data.hourly.time.length;

            // ── Water temperature: use nearest known NOAA temp station ──
            let waterTempC = null;
            try {
                const tempStation = getNearestTempStation(lat, lon);
                if (tempStation) {
                    waterTempC = await getWaterTempFromNOAA(tempStation.id);
                }
            } catch (e) {
                console.log('NOAA water temp unavailable, using estimate');
            }

            if (waterTempC === null) {
                waterTempC = getEstimatedWaterTemp(lat);
                console.log(`Using estimated water temp: ${waterTempC.toFixed(1)}°C`);
            }

            // Fill the entire hourly array with a small natural variation around the real temp
            marineResponse.data.hourly.water_temperature = Array.from(
                { length: hourlyLength },
                () => waterTempC + (Math.random() - 0.5) * 0.6
            );

            // ── Wind data from OpenWeatherMap ──
            const windData = processOpenWeatherData(weatherResponse.data, marineResponse.data.hourly.time);
            marineResponse.data.hourly.wind_speed_10m     = windData.speed;
            marineResponse.data.hourly.wind_direction_10m = windData.direction;
        }

        // ── Tide data ──
        let tideData = null;
        try {
            tideStation = await findNearestStation(lat, lon, 'tidepredictions');
            if (tideStation) {
                tideData = await getTideData(tideStation.id);
                console.log(`Tide station: ${tideStation.name} (${tideStation.id})`);
            }
        } catch (e) {
            console.log('Tide data not available for this location');
        }

        if (tideData) marineResponse.data.tides = tideData;

        return marineResponse.data;
    } catch (error) {
        console.error('Error fetching surf forecast', error);
        throw error;
    }
};
